// @file Vue component mixin that allow our VueJS padlet surface to work on the mobile app
// through postMessage and JS callbacks executed by WebView programmatically.
/* eslint-disable class-methods-use-this */
import appCan from 'bits/app_can'
import { isAppUsing } from 'bits/flip'
import window from 'bits/global'
import { buildUrlFromPath, currentPath, navigateTo } from 'bits/location'
import {
  convertLayoutResult,
  editPost,
  expandPost,
  firstTouch,
  openLink,
  openMapLocationPicker,
  pageReady,
  reportCopyPostResult,
  reportTransferPostResult,
  saveSettingsResult,
  showCommentsPanel,
  updateActiveContributorsList,
  updateAddress,
  updateBackground,
} from 'native_bridge/actions'
import { createClickListener } from 'native_bridge/anchor'
import { listen } from 'native_bridge/listen'
import postMessage from 'native_bridge/post_message'
import { ListenedMessageHandler } from 'native_bridge/types'
import {
  createCommentMenuAlertSheet,
  createCopyPostSidePanel,
  createPostActionAlertSheet,
  createSectionMenuAlertSheet,
  createTransferPostSidePanel,
  PostActionMenuItem,
  showNativeUI,
} from 'native_bridge/ui'
import * as sorter from 'surface/sorter'
import { Post, User } from 'types'
import { Component, Vue, Watch } from 'vue-property-decorator'
import DomContentLoadedMixin from 'vuemixins/dom_content_loaded_mixin'
import { Action, Getter, namespace, State } from 'vuex-class'
import { VALID_COLORS } from 'vuexstore/helpers/post'
import { CopyOrTransferPostResult } from 'vuexstore/surface/types'

type CopyOrTransferType = 'new' | 'current' | 'other'

const comment = namespace('comment')
const section = namespace('section')
const nativeApp = namespace('nativeApp')
const settings = namespace('settings')
const post = namespace('post')
const postAction = namespace('postAction')
const postConnection = namespace('postConnection')
const map = namespace('map')
const contributingStatus = namespace('contributingStatus')

@Component({
  mixins: [DomContentLoadedMixin],
})
class NativeAppMixin extends Vue {
  @State private constants
  @State private xCommentsPanel
  @State private commentsPanelPostId
  @State private wall
  @State private isIOS
  @State private isAndroid
  @Getter private address
  @Getter private background
  @Getter private convertLayoutErrorMessage
  @Getter private format
  @Action private refresh
  @Action private setCoverPost
  @Action private unsetCoverPost
  @Action private hideCommentsPanel

  @comment.State private actionMenuCommentId
  @comment.Action private startEditingComment
  @comment.Action private deleteComment

  @section.State private sectionEntities
  @section.State private sectionBeingEditedId
  @section.State private actionMenuSectionId
  @section.Action startEditingSection
  @section.Action moveSectionLeft
  @section.Action moveSectionRight
  @section.Action deleteSection

  @settings.Getter private isSaving
  @settings.Getter private saveErrorMessage

  @map.Getter private isPickingLocation
  @map.Action private startPickingLocation
  @map.Action private stopPickingLocation
  @map.Action private pickLocation

  @nativeApp.State public linkToOpen
  @nativeApp.State private alertSheetManifest
  @nativeApp.State private sidePanelManifest
  @nativeApp.Getter private expandedPost
  @nativeApp.Action private clearOpenLink
  @nativeApp.Action private hideAllPanelsAndMenus
  @nativeApp.Action private updateShelfSectionBottomPadding
  @nativeApp.Action private postSurfaceState

  @post.Getter private postsArray
  @post.Getter private postBeingEdited
  @post.Action private startEditingPost
  @post.Action private deletePost
  @post.Action private sortedPost
  @post.Action private removePostColor
  @post.Action private changePostColor

  @postAction.State private copyOrTransferPostResult
  @postAction.Getter private postUnderAction
  @postAction.Getter private canEditPostUnderAction
  @postAction.Getter private canConnectPostUnderAction
  @postAction.Getter private canDisconnectPostUnderAction
  @postAction.Getter private canChangePostStackOrder
  @postAction.Getter private canTransferPostUnderAction
  @postAction.Getter private canCopyPostUnderAction
  @postAction.Getter private canChangeCoverToPostUnderAction
  @postAction.Getter private canChangePostUnderActionLocation
  @postAction.Getter private isPostUnderActionPreviewPost
  @postAction.Getter private canDeletePostUnderAction
  @postAction.Action private expandPost
  @postAction.Action private startPostTransfer
  @postAction.Action private startPostCopy
  @postAction.Action private hidePostActionMenu

  @postConnection.Action private startConnection
  @postConnection.Action private startDisconnection

  @contributingStatus.Getter private activeContributorsList
  @Watch('address')
  public respondToAddressChange(newValue: string, oldValue: string): void {
    postMessage(updateAddress(newValue))
  }

  @Watch('linkToOpen')
  public respondToLinkToOpenChange(newValue, oldValue): void {
    if (newValue && !oldValue) {
      const { linkType, url, contentCategory, contentSubcategory } = newValue
      postMessage(openLink(url, linkType, contentCategory, contentSubcategory))
      this.clearOpenLink()
    }
  }

  @Watch('expandedPost')
  private respondToSettingExpandedPostManifest(newExpandedPost): void {
    if (!newExpandedPost) return
    const { wishFrameUrlTemplate } = this.constants
    postMessage(
      expandPost({
        expandedPost: newExpandedPost,
        allPosts: this.postsArray,
        wall: this.wall,
        window,
        wishFrameUrlTemplate,
      }),
    )
  }

  @Watch('format')
  public respondToLayoutChange(): void {
    postMessage(convertLayoutResult({ status: 'success' }))
  }

  @Watch('convertLayoutErrorMessage')
  public respondToLayoutChangeError(errorMessage): void {
    if (errorMessage) {
      postMessage(
        convertLayoutResult({
          status: 'error',
          error_reason: errorMessage,
        }),
      )
    }
  }

  @Watch('background')
  public respondToBackgroundColorChange(newValue): void {
    postMessage(updateBackground(newValue))
  }

  @Watch('actionMenuCommentId')
  private respondToActionMenuCommentId(actionMenuCommentId: number | null): void {
    if (!actionMenuCommentId) return
    this.listenToCommentActionMenu(actionMenuCommentId)
    showNativeUI(createCommentMenuAlertSheet(actionMenuCommentId))
  }

  private listenToCommentActionMenu(commentUnderActionId: number): void {
    listen('edit_comment', (): void => {
      this.startEditingComment({ commentId: commentUnderActionId })
    })
    listen('delete_comment', (): void => {
      this.deleteComment({ commentId: commentUnderActionId })
    })
  }

  @Watch('actionMenuSectionId')
  private respondToActionMenuSectionId(actionMenuSectionId: number | null): void {
    if (!actionMenuSectionId) return
    this.listenToSectionActionMenu(actionMenuSectionId)
    showNativeUI(createSectionMenuAlertSheet(actionMenuSectionId))
  }

  private listenToSectionActionMenu(sectionUnderActionId: number): void {
    listen('edit_section', (): void => {
      this.startEditingSection({ sectionId: sectionUnderActionId })
    })
    listen('move_section_left', (): void => {
      this.moveSectionLeft({ sectionId: sectionUnderActionId })
    })
    listen('move_section_right', (): void => {
      this.moveSectionRight({ sectionId: sectionUnderActionId })
    })
    listen('delete_section', (): void => {
      this.deleteSection({ sectionId: sectionUnderActionId })
    })
  }

  @Watch('postBeingEdited')
  private respondToPostBeingEditedChange(currentPost: Post, lastPost: Post): void {
    if (!currentPost) return
    if (!lastPost || currentPost.cid !== lastPost.cid) {
      postMessage(editPost(currentPost))
    }
  }

  @Watch('activeContributorsList')
  private respondToActiveContributorsListChanged(currentList: Partial<User>[], _oldList: Partial<User>[]): void {
    postMessage(updateActiveContributorsList(currentList))
  }

  @Watch('isPickingLocation')
  private respondToIsPickingLocation(isPickingLocation: boolean): void {
    if (!appCan('useNativeMapLocationPicker')) return
    if (!isPickingLocation) return
    postMessage(openMapLocationPicker())
    listen(
      'map_location_picked',
      async ({ location, name }): Promise<void> => {
        if (location && name) {
          const LatLng = (await import('bits/map')).LatLng
          this.pickLocation({ latLng: new LatLng(location), name })
        }
        this.stopPickingLocation()
      },
    )
  }

  @Watch('isSaving')
  public respondToSettingsSave(isSaving: boolean, wasSaving: boolean): void {
    const justSaved = !isSaving && wasSaving
    if (!justSaved) return

    let result: { [key: string]: unknown } = {}
    if (this.saveErrorMessage) {
      result = {
        status: 'error',
        error_reason: this.saveErrorMessage,
      }
    } else {
      result = {
        status: 'success',
        wall: this.wall,
      }
    }
    postMessage(saveSettingsResult(result))
  }

  @Watch('postUnderAction')
  private respondToPostUnderAction(postUnderAction: Post): void {
    if (!postUnderAction) return
    // 1. Listen to events
    this.listenToPostUnderActionMenu(postUnderAction)
    // 2. create the postUnderAction action menu manifest here and show it
    const wall = {
      publicKey: this.wall.public_key,
      themeColors: VALID_COLORS,
      colorScheme: this.wall.color_scheme,
      mobileAppCopyPostUrlTemplate: this.constants.mobileAppCopyPostUrlTemplate,
      mobileAppTransferPostUrlTemplate: this.constants.mobileAppTransferPostUrlTemplate,
    }

    // Specify which options will appear in the alert sheet.
    const sheetOptions: PostActionMenuItem[] = []
    if (this.canEditPostUnderAction) {
      sheetOptions.push(PostActionMenuItem.Colors)
      sheetOptions.push(PostActionMenuItem.Edit)
    }
    if (this.canChangePostUnderActionLocation) {
      sheetOptions.push(PostActionMenuItem.ChangeLocation)
    }
    if (this.canConnectPostUnderAction) {
      sheetOptions.push(PostActionMenuItem.Connect)
    }
    if (this.canDisconnectPostUnderAction) {
      sheetOptions.push(PostActionMenuItem.Disconnect)
    }
    if (this.canChangePostStackOrder) {
      sheetOptions.push(PostActionMenuItem.BringPostToFront)
      sheetOptions.push(PostActionMenuItem.SendPostToBack)
    }
    if (this.canEditPostUnderAction || this.canConnectPostUnderAction || this.canDisconnectPostUnderAction) {
      sheetOptions.push(PostActionMenuItem.Divider)
    }
    if (this.canTransferPostUnderAction) {
      sheetOptions.push(PostActionMenuItem.Transfer)
    }
    if (this.canCopyPostUnderAction) {
      sheetOptions.push(PostActionMenuItem.Copy)
    }
    sheetOptions.push(PostActionMenuItem.Expand)
    if (this.canChangeCoverToPostUnderAction) {
      sheetOptions.push(PostActionMenuItem.Divider)
      if (this.isPostUnderActionPreviewPost) {
        sheetOptions.push(PostActionMenuItem.UnsetAsCover)
      } else {
        sheetOptions.push(PostActionMenuItem.SetAsCover)
      }
    }
    if (this.canDeletePostUnderAction) {
      sheetOptions.push(PostActionMenuItem.Divider)
      sheetOptions.push(PostActionMenuItem.Delete)
    }

    const { id: postId, cid: postCid, author_id: postAuthorId } = postUnderAction
    const id = Number(postId)
    const cid = postCid as string
    const author_id = Number(postAuthorId)

    const alertSheet = createPostActionAlertSheet({ id, cid, author_id }, wall, sheetOptions)
    showNativeUI(alertSheet)
  }

  private listenToPostUnderActionMenu(postUnderAction: Post): void {
    const postId = Number(postUnderAction.id)
    const postCid = postUnderAction.cid
    const authorId = Number(postUnderAction.author_id)
    const hideMenuFirst = (wrapped: Function): ListenedMessageHandler => {
      return (msg: unknown): void => {
        this.hidePostActionMenu()
        wrapped(msg)
      }
    }
    listen(
      'edit_post',
      hideMenuFirst((): void => {
        this.startEditingPost({ postCid })
      }),
    )
    listen(
      'connect_post',
      hideMenuFirst((): void => {
        this.startConnection({ postId })
      }),
    )
    listen(
      'disconnect_post',
      hideMenuFirst((): void => {
        this.startDisconnection({ postId })
      }),
    )
    listen(
      'bring_post_to_front',
      hideMenuFirst((): void => {
        if (isAppUsing('wishOrderArray')) {
          this.sortedPost({ postCid, to: 'front' })
        }
        if (!isAppUsing('wishOrderArray')) {
          this.sortedPost({ postCid, sortIndex: sorter.getTopSortIndex() })
        }
      }),
    )
    listen(
      'send_post_to_back',
      hideMenuFirst((): void => {
        if (isAppUsing('wishOrderArray')) {
          this.sortedPost({ postCid, to: 'back' })
        }
        if (!isAppUsing('wishOrderArray')) {
          this.sortedPost({ postCid, sortIndex: sorter.getBottomSortIndex() })
        }
      }),
    )
    listen(
      'expand_post',
      hideMenuFirst((): void => {
        this.expandPost({ postCid })
      }),
    )
    listen(
      'delete_post',
      hideMenuFirst((): void => {
        this.deletePost({ postCid })
      }),
    )
    listen(
      'update_post_color',
      hideMenuFirst((msg): void => {
        this.changePostColor({ postCid, newBgColor: msg.label })
      }),
    )
    listen(
      'remove_post_color',
      hideMenuFirst((): void => {
        this.removePostColor({ postCid })
      }),
    )
    listen(
      'set_as_cover',
      hideMenuFirst((): void => {
        this.setCoverPost({ postId })
      }),
    )
    listen(
      'unset_as_cover',
      hideMenuFirst((): void => {
        this.unsetCoverPost({ postId })
      }),
    )
    listen(
      'change_location',
      hideMenuFirst((): void => {
        this.startPickingLocation({ postCid })
      }),
    )
    listen(
      'transfer_post',
      hideMenuFirst((): void => {
        // track TODO
        showNativeUI(
          createTransferPostSidePanel(this.constants.mobileAppTransferPostUrlTemplate, this.wall, {
            id: postId,
            author_id: authorId,
          }),
        )
        this.startPostTransfer({ transferPostCid: postCid })
      }),
    )
    listen('copy_post', (): void => {
      // track TODO
      showNativeUI(
        createCopyPostSidePanel(this.constants.mobileAppCopyPostUrlTemplate, this.wall, {
          id: postId,
          author_id: authorId,
        }),
      )
      this.startPostCopy({ copyPostCid: postCid })
    })
  }

  @Watch('copyOrTransferPostResult')
  private respondToCopyingOrTransferringPost(copyOrTransferPostResult: CopyOrTransferPostResult): void {
    if (!copyOrTransferPostResult) return
    const { isDestinationNewWall, isDestinationThisWall, status, url, text, operation } = copyOrTransferPostResult
    let actionType: CopyOrTransferType = 'other'
    if (isDestinationNewWall) {
      actionType = 'new'
    } else if (isDestinationThisWall) {
      actionType = 'current'
    }
    if (operation === 'copy') {
      postMessage(
        reportCopyPostResult({
          copy_type: actionType,
          status,
          url,
          text,
        }),
      )
    } else if (operation === 'transfer') {
      postMessage(
        reportTransferPostResult({
          transfer_type: actionType,
          status,
          url,
          text,
        }),
      )
    } else {
      throw new Error(`Unknown operation: ${operation}`)
    }
  }

  @Watch('xCommentsPanel')
  private respondToShowCommentsPanel(newXCommentsPanel: boolean): void {
    // older versions of the app should still show the web based comments panel
    if (!appCan('useNativeCommentsPanel')) {
      return
    }

    if (newXCommentsPanel) {
      postMessage(showCommentsPanel(this.commentsPanelPostId))
      this.hideCommentsPanel()
    }
  }

  @Watch('isDomLoaded', { immediate: true })
  private postMessageOnDomReady(isReady): void {
    if (!isReady) return
    if (!(this.isIOS || this.isAndroid)) return
    if (currentPath().includes('beethoven/attach') || currentPath().includes('mobile-app/attach')) return

    const isDisplayedInIFrame = window.self !== window.top
    if (isDisplayedInIFrame) return

    postMessage(pageReady(), () => navigateTo(buildUrlFromPath('/ios-app/page-ready')))
  }

  // sectionEntities -- for when there is any change in sections (because of realtime, etc).
  // sectionBeingEditedId -- for when a section is done being edited.
  @Watch('sectionBeingEditedId')
  @Watch('sectionEntities')
  private surfaceStateUpdated(): void {
    // if a section is being edited, don't post updated app state.
    // only if editing is completed, do we post app state.
    if (!!this.sectionBeingEditedId) return
    this.postSurfaceState()
  }

  private mounted(): void {
    // Make all body anchor tags listen to click events and
    // respond to native app
    document.body.addEventListener('click', createClickListener(this.$store))
    this.listenForFirstTouch()
    this.installGlobalListeners()
  }

  // Collapses header on first touch
  private listenForFirstTouch(): void {
    if (!appCan('useNativeHeader')) return

    const listener = (): void => {
      postMessage(firstTouch())
      document.body.removeEventListener('touchend', listener)
    }

    document.body.addEventListener('touchend', listener)
  }

  private installGlobalListeners(): void {
    listen('refresh_padlet', this.refresh)
    listen('cancel_show', this.hideAllPanelsAndMenus)
    listen('update_shelf_section_bottom_padding', this.updateShelfSectionBottomPadding)
    listen('post_surface_state', this.postSurfaceState)
  }
}

export default NativeAppMixin
